import React, { useState } from 'react';
import { PlayIcon, PauseIcon, RefreshCwIcon, MoreVerticalIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, ClockIcon, TrendingUpIcon, FilterIcon, PlusIcon, EditIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function OrchestrationWorkflowPage() {
  const navigate = useNavigate();
  const [selectedOrchestration, setSelectedOrchestration] = useState<number | null>(null);
  const orchestrations = [{
    id: 1,
    name: 'In-Market Takeover',
    status: 'active',
    triggers: 'Signal threshold',
    lastRun: '5 minutes ago',
    successRate: 94.5,
    medianLatency: 3.2,
    errors: 2,
    owner: 'Sarah Chen',
    version: 'v2.3',
    totalRuns: 1247
  }, {
    id: 2,
    name: 'Export to Salesloft + Cadence',
    status: 'active',
    triggers: 'Segment entry',
    lastRun: '12 minutes ago',
    successRate: 98.2,
    medianLatency: 2.8,
    errors: 0,
    owner: 'Mike Rodriguez',
    version: 'v1.5',
    totalRuns: 892
  }, {
    id: 3,
    name: 'High-Intent Account Blitz',
    status: 'paused',
    triggers: '3+ buying signals',
    lastRun: '2 hours ago',
    successRate: 89.3,
    medianLatency: 4.5,
    errors: 8,
    owner: 'Lisa Park',
    version: 'v3.1',
    totalRuns: 2341
  }, {
    id: 4,
    name: 'Trial Activation Flow',
    status: 'active',
    triggers: 'Product usage',
    lastRun: '8 minutes ago',
    successRate: 96.7,
    medianLatency: 1.9,
    errors: 1,
    owner: 'John Smith',
    version: 'v2.0',
    totalRuns: 567
  }, {
    id: 5,
    name: 'Executive Engagement',
    status: 'error',
    triggers: 'C-level identified',
    lastRun: '1 hour ago',
    successRate: 72.4,
    medianLatency: 8.2,
    errors: 15,
    owner: 'Sarah Chen',
    version: 'v1.8',
    totalRuns: 234
  }];
  const runHistory = [{
    id: 1,
    timestamp: '2024-01-15 14:32:15',
    input: 'Account: Acme Corp',
    status: 'success',
    duration: 2.8,
    nodes: 4
  }, {
    id: 2,
    timestamp: '2024-01-15 14:28:42',
    input: 'Account: TechStart Inc',
    status: 'success',
    duration: 3.1,
    nodes: 4
  }, {
    id: 3,
    timestamp: '2024-01-15 14:25:18',
    input: 'Account: Global Solutions',
    status: 'failed',
    duration: 5.2,
    nodes: 2,
    error: 'API timeout on Salesloft export'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'paused':
        return 'bg-yellow-100 text-yellow-700';
      case 'error':
        return 'bg-red-100 text-red-700';
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <PlayIcon className="w-3 h-3" data-id="element-5421" />;
      case 'paused':
        return <PauseIcon className="w-3 h-3" data-id="element-5422" />;
      case 'error':
        return <XCircleIcon className="w-3 h-3" data-id="element-5423" />;
      default:
        return <CheckCircleIcon className="w-3 h-3" data-id="element-5424" />;
    }
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-5425">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-5426">
        <div className="flex items-center justify-between" data-id="element-5427">
          <div data-id="element-5428">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5429">
              Orchestration Workflow
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-5430">
              Operate and monitor orchestrations with health metrics and run
              history
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-5431">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5432">
              <FilterIcon className="w-4 h-4" data-id="element-5433" />
              Filter
            </button>
            <button onClick={() => navigate('/orchestration/editor')} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-5434">
              <PlusIcon className="w-4 h-4" data-id="element-5435" />
              New Orchestration
            </button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="p-6" data-id="element-5436">
        <div className="grid grid-cols-3 gap-6" data-id="element-5437">
          {/* Orchestrations List */}
          <div className="col-span-2 space-y-4" data-id="element-5438">
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-5439">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-5440">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-5441">
                  Active Orchestrations
                </h2>
              </div>
              <div className="overflow-x-auto" data-id="element-5442">
                <table className="w-full" data-id="element-5443">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-5444">
                    <tr data-id="element-5445">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-5446">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-5447">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-5448">
                        Success Rate
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-5449">
                        Latency
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-5450">
                        Errors
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-5451">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-5452">
                    {orchestrations.map(orch => <tr key={orch.id} className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedOrchestration === orch.id ? 'bg-blue-50' : ''}`} onClick={() => setSelectedOrchestration(orch.id)} data-id="element-5453">
                        <td className="px-4 py-4" data-id="element-5454">
                          <div data-id="element-5455">
                            <p className="text-sm font-semibold text-gray-900" data-id="element-5456">
                              {orch.name}
                            </p>
                            <p className="text-xs text-gray-600" data-id="element-5457">
                              {orch.owner} • {orch.version}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4" data-id="element-5458">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(orch.status)}`} data-id="element-5459">
                            {getStatusIcon(orch.status)}
                            {orch.status}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-5460">
                          <div className="flex items-center gap-2" data-id="element-5461">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-5462">
                              <div className="h-full bg-green-600 rounded-full" style={{
                            width: `${orch.successRate}%`
                          }} data-id="element-5463" />
                            </div>
                            <span className="text-sm font-semibold text-gray-900" data-id="element-5464">
                              {orch.successRate}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4" data-id="element-5465">
                          <p className="text-sm text-gray-900" data-id="element-5466">
                            {orch.medianLatency}s
                          </p>
                        </td>
                        <td className="px-4 py-4" data-id="element-5467">
                          {orch.errors > 0 ? <span className="flex items-center gap-1 text-sm font-semibold text-red-600" data-id="element-5468">
                              <AlertTriangleIcon className="w-4 h-4" data-id="element-5469" />
                              {orch.errors}
                            </span> : <span className="text-sm text-gray-600" data-id="element-5470">—</span>}
                        </td>
                        <td className="px-4 py-4" data-id="element-5471">
                          <button className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-5472">
                            <MoreVerticalIcon className="w-4 h-4 text-gray-600" data-id="element-5473" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Run History */}
            {selectedOrchestration && <div className="bg-white rounded-lg border border-gray-200" data-id="element-5474">
                <div className="px-6 py-4 border-b border-gray-200" data-id="element-5475">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-5476">
                    Recent Runs
                  </h3>
                </div>
                <div className="divide-y divide-gray-200" data-id="element-5477">
                  {runHistory.map(run => <div key={run.id} className="px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-5478">
                      <div className="flex items-start justify-between mb-2" data-id="element-5479">
                        <div data-id="element-5480">
                          <p className="text-sm font-medium text-gray-900" data-id="element-5481">
                            {run.input}
                          </p>
                          <p className="text-xs text-gray-600" data-id="element-5482">
                            {run.timestamp}
                          </p>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-5483">
                          {run.status === 'success' ? <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-5484" /> : <XCircleIcon className="w-4 h-4 text-red-600" data-id="element-5485" />}
                          <span className="text-xs text-gray-600" data-id="element-5486">
                            {run.duration}s
                          </span>
                        </div>
                      </div>
                      {run.error && <div className="p-2 bg-red-50 rounded border border-red-200" data-id="element-5487">
                          <p className="text-xs text-red-700" data-id="element-5488">{run.error}</p>
                        </div>}
                      <div className="flex items-center gap-2 mt-2" data-id="element-5489">
                        <span className="text-xs text-gray-600" data-id="element-5490">
                          {run.nodes} nodes executed
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
          {/* Health & Controls Sidebar */}
          <div className="space-y-4" data-id="element-5491">
            {selectedOrchestration ? <>
                {/* Controls */}
                <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-5492">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-5493">
                    Controls
                  </h3>
                  <div className="space-y-2" data-id="element-5494">
                    <button className="w-full flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" data-id="element-5495">
                      <PlayIcon className="w-4 h-4" data-id="element-5496" />
                      Enable
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5497">
                      <PauseIcon className="w-4 h-4" data-id="element-5498" />
                      Pause
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5499">
                      <RefreshCwIcon className="w-4 h-4" data-id="element-5500" />
                      Run Test
                    </button>
                    <button onClick={() => navigate('/orchestration/editor')} className="w-full flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5501">
                      <EditIcon className="w-4 h-4" data-id="element-5502" />
                      Edit Flow
                    </button>
                  </div>
                </div>
                {/* Health Metrics */}
                <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-5503">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-5504">
                    Health Metrics
                  </h3>
                  <div className="space-y-3" data-id="element-5505">
                    <div data-id="element-5506">
                      <div className="flex items-center justify-between mb-1" data-id="element-5507">
                        <span className="text-xs text-gray-600" data-id="element-5508">
                          Success Rate
                        </span>
                        <span className="text-xs font-semibold text-gray-900" data-id="element-5509">
                          94.5%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-5510">
                        <div className="h-full bg-green-600 rounded-full" style={{
                      width: '94.5%'
                    }} data-id="element-5511" />
                      </div>
                    </div>
                    <div data-id="element-5512">
                      <div className="flex items-center justify-between mb-1" data-id="element-5513">
                        <span className="text-xs text-gray-600" data-id="element-5514">
                          Avg Latency
                        </span>
                        <span className="text-xs font-semibold text-gray-900" data-id="element-5515">
                          3.2s
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-5516">
                        <div className="h-full bg-blue-600 rounded-full" style={{
                      width: '64%'
                    }} data-id="element-5517" />
                      </div>
                    </div>
                    <div data-id="element-5518">
                      <div className="flex items-center justify-between mb-1" data-id="element-5519">
                        <span className="text-xs text-gray-600" data-id="element-5520">
                          Error Rate
                        </span>
                        <span className="text-xs font-semibold text-gray-900" data-id="element-5521">
                          0.16%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-5522">
                        <div className="h-full bg-red-600 rounded-full" style={{
                      width: '16%'
                    }} data-id="element-5523" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Usage & Cost */}
                <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-5524">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-5525">
                    Usage & Cost
                  </h3>
                  <div className="space-y-3" data-id="element-5526">
                    <div className="flex items-center justify-between" data-id="element-5527">
                      <span className="text-xs text-gray-600" data-id="element-5528">API Calls</span>
                      <span className="text-sm font-semibold text-gray-900" data-id="element-5529">
                        4,988
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5530">
                      <span className="text-xs text-gray-600" data-id="element-5531">
                        Est. Cost/1K
                      </span>
                      <span className="text-sm font-semibold text-gray-900" data-id="element-5532">
                        $2.40
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5533">
                      <span className="text-xs text-gray-600" data-id="element-5534">Total Runs</span>
                      <span className="text-sm font-semibold text-gray-900" data-id="element-5535">
                        1,247
                      </span>
                    </div>
                  </div>
                </div>
              </> : <div className="bg-white rounded-lg border border-gray-200 p-6 text-center" data-id="element-5536">
                <p className="text-sm text-gray-600" data-id="element-5537">
                  Select an orchestration to view details
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}